import Cookies from "js-cookie";
import _cloneDeep from "lodash.clonedeep";
import { createContext, Dispatch, useContext, useReducer } from "react";
import settings from "src/settings";
import { ILeague } from "./useLeague";
import { PlayoffsEntry } from "./useSession";

const {
  COOKIE_NAMES: { nativeTokenCookieName },
} = settings;

const initialState: ClientState = {
  gigyaProfile: null,
  openModal: { name: null, options: {} },
  currentEntry: null,
  isNative: false,
  // errorToast
  // isPencilBarEnabled
  // isFetching
};

const ClientContext = createContext(initialState);
const ClientDispatchContext = createContext(null);

function clientStateReducer(state: ClientState, action: ClientAction): ClientState {
  switch (action.type) {
    case ACTIONS.UPDATE_GIGYA_PROFILE: {
      return { ...state, gigyaProfile: action.profile };
    }
    case ACTIONS.SET_MODAL: {
      return {
        ...state,
        openModal: {
          name: action.modal.name,
          options: {
            ...(state.openModal.options || {}),
            ...(action.modal.options || {}),
          },
        },
      };
    }
    case ACTIONS.SET_CURRENT_ENTRY: {
      return { ...state, currentEntry: action.entry };
    }
    default: {
      throw new Error(`Unhandled action in clientStateReducer: ${action}`);
    }
  }
}

/**
 * Client application state provider.
 */
export function ClientContextProvider({ children, ...appProps }) {
  const initialStateWithProps = _cloneDeep(initialState);
  const nativeToken = appProps.nativeToken || Cookies.get(nativeTokenCookieName) || "";

  const isNative = Boolean(appProps.isNative || (typeof window !== "undefined" && window.isNative));

  initialStateWithProps.isNative = isNative;

  if (nativeToken) {
    initialStateWithProps.gigyaProfile = {
      ...(initialStateWithProps.gigyaProfile || {}),
      gigya_id: nativeToken,
    };
  }

  const [state, dispatch] = useReducer(clientStateReducer, initialStateWithProps);

  return (
    <ClientContext.Provider value={state}>
      <ClientDispatchContext.Provider value={dispatch}>{children}</ClientDispatchContext.Provider>
    </ClientContext.Provider>
  );
}

function useClientState() {
  const context = useContext<ClientState>(ClientContext);
  if (context === undefined) {
    throw new Error("useClientState must be used within a ClientContext Provider");
  }
  return context;
}

function useClientDispatch() {
  const context = useContext<Dispatch<ClientAction>>(ClientDispatchContext);
  if (context === undefined) {
    throw new Error("useClientDispatch must be used within a ClientDispatchContext Provider");
  }
  return context;
}

/**
 * Main hook to useContext for the client-side application state and dispatching changes.
 * @example const [state, dispatch] = useClientContext()
 */
export default function useClientContext(): [ClientState, Dispatch<ClientAction>] {
  return [useClientState(), useClientDispatch()];
}

type ClientState = {
  gigyaProfile: GigyaProfilePayload | null;
  currentEntry: PlayoffsEntry | null;
  openModal: Modals;
  isNative: boolean;
};

enum ACTIONS {
  UPDATE_GIGYA_PROFILE = "UPDATE_GIGYA_PROFILE",
  SET_MODAL = "SET_MODAL",
  SET_CURRENT_ENTRY = "SET_CURRENT_ENTRY",
  // SHOW_ERROR = "SHOW_ERROR",
}

type ModalNames =
  | "CREATE_ENTRY"
  | "EDIT_ENTRY"
  | "DRIVER_MODAL"
  | "PICKS_COMPLETE"
  | "CREATE_LEAGUE"
  | "INVITE_MODAL"
  | "JOIN_LEAGUE"
  | "LEAVE_LEAGUE";

interface ModalOptions {
  driverId?: number | null;
  hasShownPicksComplete?: boolean;
  league?: ILeague;
}

type Modals = {
  name: ModalNames | null;
  options?: ModalOptions;
};

/**
 * This interface shapes the payload needed to hit sso.json
 */
interface GigyaProfilePayload {
  gigya_id?: string;
  signature?: string;
  timestamp?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  zip?: string;
}

//* ↓ Reducer methods to use throughout the app to dispatch global state changes ↓

type ClientAction = UpdateProfileAction | SetModalAction | SetCurrentEntryAction;

interface UpdateProfileAction {
  type: ACTIONS.UPDATE_GIGYA_PROFILE;
  profile: ClientState["gigyaProfile"];
}
/**
 * Update the Gigya profile in state.
 */
export const updateGigyaProfile = (profile: ClientState["gigyaProfile"]): UpdateProfileAction => ({
  type: ACTIONS.UPDATE_GIGYA_PROFILE,
  profile,
});

interface SetModalAction {
  type: ACTIONS.SET_MODAL;
  modal: ClientState["openModal"];
}
/**
 * Set the current visible modal (or null to hide).
 */
export const setModal = (name: ModalNames | null, options?: ModalOptions): SetModalAction => ({
  type: ACTIONS.SET_MODAL,
  modal: {
    name,
    options,
  },
});

interface SetCurrentEntryAction {
  type: ACTIONS.SET_CURRENT_ENTRY;
  entry: ClientState["currentEntry"];
}
/**
 * Set the active user entry.
 */
export const setCurrentEntry = (entry: ClientState["currentEntry"]): SetCurrentEntryAction => ({
  type: ACTIONS.SET_CURRENT_ENTRY,
  entry,
});

declare global {
  interface Window {
    isNative?: boolean;
  }
}
